import { UiLibraryModule } from '@template-ui/template-ui.module';
import { HearingPhaseBarDirective } from './hearing-phase-bar.directive';
import { SdmfAnalysisSectionComponent } from '../hearing-record/sdmf-analysis-section.component';
import { RoleService } from '@app/shared/services/roles.service';
import { SDMF_FEATURE_FLAG } from '../hearing-record/sdmf-analysis.models';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PhmsCaseDocumentsComponent } from '../documents/phms-case-documents.component';
import { HearingCycleContextStore } from './hearing-cycle-context.store';
import {
  HearingSharedView,
  isSharedHearingView,
} from './hearing-navigation.service';

function sharedViewOrNull(value: unknown): HearingSharedView | null {
  return isSharedHearingView(value) ? value : null;
}

/**
 * The hearing-wide SDMF analysis and Documents views. Hosted in the SDMF and Documents tab panels of
 * every phase page (#1530); `view` selects which one renders, falling back to the route's `phase`
 * data for any remaining route-level use.
 */
@Component({
  selector: 'phms-hearing-shared-content',
  standalone: true,
  imports: [
    CommonModule,
    UiLibraryModule,
    HearingPhaseBarDirective,
    PhmsCaseDocumentsComponent,
    SdmfAnalysisSectionComponent,
  ],
  host: {
    '[attr.data-testid]': "'hearing-tab-panel-' + activeView()",
    '[attr.data-section]': 'activeView()',
  },
  template: `
    <ng-container *ngIf="context.error()">
      <div *hearingPhaseBar="'actions'">
        <ui-button
          buttonText="Retry hearing context"
          severity="secondary"
          (click)="retryContext()"
        ></ui-button>
      </div>
    </ng-container>
    <ng-container
      *ngIf="context.record() && !context.isLoading() && !context.error()"
    >
      <ng-container *ngIf="activeView() === 'sdmf'">
        <phms-sdmf-analysis-section
          [authoringEnabled]="sdmfEnabled()"
          [scheduledProceedingId]="context.scheduledProceedingId()"
        ></phms-sdmf-analysis-section>
      </ng-container>
      <phms-case-documents
        *ngIf="activeView() === 'documents'"
        [embedded]="true"
        [incarceratedPersonId]="personId()"
        [subjectName]="subjectName()"
      ></phms-case-documents>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        padding: 0 var(--phms-workspace-gutter, 1rem) 1rem;
        color: var(--custom-text-color);
      }
    `,
  ],
})
export class HearingSharedContentComponent {
  readonly context = inject(HearingCycleContextStore);
  private readonly roles = inject(RoleService);
  private readonly routeView = sharedViewOrNull(
    inject(ActivatedRoute).snapshot?.data?.['phase'],
  );
  readonly view = input<HearingSharedView | null>(null);
  readonly activeView = computed<HearingSharedView | null>(
    () => this.view() ?? this.routeView,
  );
  readonly sdmfEnabled = computed(() =>
    this.roles.isFeatureEnabled(SDMF_FEATURE_FLAG),
  );
  retryContext(): void {
    if (this.context.error() && !this.context.isLoading())
      this.context.refresh().subscribe();
  }
  readonly personId = computed(
    () => this.context.record()?.incarceratedPersonId ?? null,
  );
  readonly subjectName = computed(() => {
    const demographics = this.context.record()?.context?.demographics;
    return (
      demographics?.fullName?.trim() ||
      [demographics?.firstName, demographics?.lastName]
        .filter(Boolean)
        .join(' ')
    );
  });
}
